// Portions of this code are adapted from the MUI Toolbar component.
// Source: https://github.com/mui/toolpad/blob/master/packages/toolpad-core/src/DashboardLayout/DashboardLayout.tsx
// MUI is licensed under the MIT License: https://github.com/mui/toolpad/blob/master/LICENSE

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SidebarContext } from './SidebarContext';

interface PropTypes {
    children: JSX.Element
}

export function SidebarProvider({
    children
}: PropTypes) {
    const theme = useTheme();

    // desktop and mobile sidebar state
    const [isDesktopNavigationExpanded, setIsDesktopNavigationExpanded] = useState(true);
    const [isMobileNavigationExpanded, setIsMobileNavigationExpanded] = useState(false);

    const isOverMdViewport = useMediaQuery(theme.breakpoints.up('md'));
    const isNavigationExpanded = isOverMdViewport ? isDesktopNavigationExpanded : isMobileNavigationExpanded;

    // state to determine when sidebar has finished expanding or collapsing
    const [isNavigationFullyExpanded, setIsNavigationFullyExpanded] = useState(isNavigationExpanded);
    const [isNavigationFullyCollapsed, setIsNavigationFullyCollapsed] = useState(!isNavigationExpanded);

    useEffect(() => {
        // when the sidebar is expanded
        if (isNavigationExpanded) {
            // set navigation fully expanded to true after the transition delay
            const drawerWidthTransitionTimeout = setTimeout(() => {
                setIsNavigationFullyExpanded(true);
            }, theme.transitions.duration.enteringScreen);

            return () => clearTimeout(drawerWidthTransitionTimeout);
        }

        setIsNavigationFullyExpanded(false);

        return () => { };
    }, [isNavigationExpanded, theme]);

    useEffect(() => {
        // when the sidebar is collapsed
        if (!isNavigationExpanded) {
            // set navigation fully collapsed to true after the transition delay
            const drawerWidthTransitionTimeout = setTimeout(() => {
                setIsNavigationFullyCollapsed(true);
            }, theme.transitions.duration.leavingScreen);

            return () => clearTimeout(drawerWidthTransitionTimeout);
        }

        setIsNavigationFullyCollapsed(false);

        return () => { };
    }, [isNavigationExpanded, theme]);

    // sets the expanded state of the sidebar
    const setIsNavigationExpanded = useCallback((newExpanded: boolean) => {
        if (isOverMdViewport) {
            setIsDesktopNavigationExpanded(newExpanded);
        } else {
            setIsMobileNavigationExpanded(newExpanded);
        }
    }, [isOverMdViewport]);

    // toggles the expanded state of the sidebar
    const toggleNavigationExpanded = useCallback(() => {
        setIsNavigationExpanded(!isNavigationExpanded);
    }, [isNavigationExpanded, setIsNavigationExpanded]);

    const value = useMemo(() => ({
        isDesktopNavigationExpanded,
        setIsDesktopNavigationExpanded,
        isMobileNavigationExpanded,
        setIsMobileNavigationExpanded,
        isNavigationExpanded,
        isNavigationFullyExpanded,
        isNavigationFullyCollapsed,
        setIsNavigationExpanded,
        toggleNavigationExpanded
    }), [
        isDesktopNavigationExpanded,
        isMobileNavigationExpanded,
        isNavigationExpanded,
        isNavigationFullyExpanded,
        isNavigationFullyCollapsed,
        setIsNavigationExpanded,
        toggleNavigationExpanded
    ]);

    return (
        <SidebarContext.Provider value={value}>
            {children}
        </SidebarContext.Provider>
    );
}
